/**
 * <drawer-component>
 *
 * Standardni drawer Custom Element. Sve drawer-e u temi (cart, mobile menu,
 * filters, sort, local pickup, sidebar favorites) renderuju kroz
 * `{% render 'drawer' %}` snippet — svi imaju isti markup pattern (sticky
 * header sa close X, scrollable body, opcioni sticky footer) i istu open/close
 * logiku.
 *
 * Feature-specifične nadogradnje (npr. cart drawer recommendations) idu kroz
 * subklasu koja extends-uje DrawerComponent i override-uje lifecycle hookove
 * (`onBeforeOpen`, `onOpened`, `onBeforeClose`, `onClosed`).
 *
 * Loaded as ES module via importmap alias `@theme/drawer-component`.
 * Učitava se globalno iz `layout/theme.liquid` da bi Custom Element bio
 * definisan na svakoj stranici (subklase mogu da extends-uju ovu baznu).
 *
 * Public DOM API
 * --------------
 *   drawerEl.open()                — otvori drawer
 *   drawerEl.close()               — zatvori drawer
 *   drawerEl.toggle()              — toggle
 *   drawerEl.isOpen                — boolean
 *   drawerEl.refreshScrollableElement() — refresh `[data-scrollable]` after async DOM replacement
 *
 * Otvaranje preko HTML atributa
 * -----------------------------
 *   <button data-action="open-drawer"  data-toggle-id="..."> — otvori
 *   <button data-action="close-drawer" data-toggle-id="..."> — zatvori
 *   <button data-action="toggle-drawer" data-toggle-id="..."> — toggle
 *   <button data-action="open-drawer" data-toggle-id="..." data-child-drawer>
 *     — otvori kao "child", ne zatvara stack druge drawere
 *
 * Bubbling events
 * ---------------
 *   drawer:before-open  (cancelable: e.preventDefault() prekida open)
 *   drawer:opened
 *   drawer:before-close (cancelable)
 *   drawer:closed
 *   drawer:opening      (na document) — koristi se za stack-anje
 *
 * Override hooks (sub-class)
 * --------------------------
 *   onBeforeOpen(event)  — return false da prekine
 *   onOpened()
 *   onBeforeClose(event) — return false da prekine
 *   onClosed()
 *
 * Dependencies:
 *   - @theme/body-scroll-lock-options
 *   - @theme/body-scroll-lock
 *   - @theme/ios-scroll-lock
 *   - @theme/focus-trap
 */

import { bodyScrollLockOptions } from '@theme/body-scroll-lock-options';
import { disableBodyScroll, enableBodyScroll } from '@theme/body-scroll-lock';
import { IOSScrollLock } from '@theme/ios-scroll-lock';
import { trapFocus, removeTrapFocus } from '@theme/focus-trap';

export class DrawerComponent extends HTMLElement {
  #scrollableEl = null;
  #overlayEl = null;
  #onOverlayClick = null;
  #onEscape = null;
  #onOtherDrawerOpening = null;
  #onTriggerClick = null;

  isOpen = false;

  connectedCallback() {
    // Opt-out: e.g. cart drawer rendered as a regular page block (cart page
    // template) should NOT behave as a drawer.
    if (this.hasAttribute('data-no-drawer')) return;

    this.refreshScrollableElement();
    this.#overlayEl = document.querySelector('.pageOverlay');

    if (!this.hasAttribute('aria-hidden')) {
      this.setAttribute('aria-hidden', 'true');
      this.setAttribute('aria-expanded', 'false');
      this.setAttribute('inert', '');
    }
    if (this.style.display !== 'none') {
      this.style.display = 'none';
    }

    this.#bindTriggers();
  }

  disconnectedCallback() {
    if (this.isOpen) {
      this.#unlockScroll();
      this.#detachGlobalListeners();
    }

    this.#unbindTriggers();
  }

  // ---------- Public API ----------

  open() {
    if (this.isOpen) return;

    const beforeEvent = new CustomEvent('drawer:before-open', {
      bubbles: true,
      cancelable: true,
    });
    this.dispatchEvent(beforeEvent);
    if (beforeEvent.defaultPrevented) return;
    if (this.onBeforeOpen?.(beforeEvent) === false) return;

    // Notify other drawers that something else is opening — they close unless
    // this is being opened as a child of an already-open drawer.
    document.dispatchEvent(
      new CustomEvent('drawer:opening', {
        detail: { drawerId: this.id, isChild: this.dataset.childDrawer != null },
      })
    );

    this.isOpen = true;
    this.style.display = '';
    this.removeAttribute('inert');
    this.setAttribute('aria-hidden', 'false');
    this.setAttribute('aria-expanded', 'true'); // legacy CSS still keys off aria-expanded
    this.classList.add('is-open');

    this.#overlayEl?.classList.add('is-visible');
    this.#overlayEl?.classList.add('is-drawer-overlay');
    this.#syncTriggerState(true);
    this.#attachGlobalListeners();
    this.#lockScroll();

    trapFocus(this);

    this.dispatchEvent(new CustomEvent('drawer:opened', { bubbles: true }));
    this.onOpened?.();
  }

  close() {
    if (!this.isOpen) return;

    const beforeEvent = new CustomEvent('drawer:before-close', {
      bubbles: true,
      cancelable: true,
    });
    this.dispatchEvent(beforeEvent);
    if (beforeEvent.defaultPrevented) return;
    if (this.onBeforeClose?.(beforeEvent) === false) return;

    this.isOpen = false;
    this.classList.remove('is-open');
    this.setAttribute('aria-expanded', 'false');

    this.#overlayEl?.classList.remove('is-visible');
    this.#overlayEl?.classList.remove('is-drawer-overlay');
    this.#syncTriggerState(false);
    this.#detachGlobalListeners();
    this.#unlockScroll();
    removeTrapFocus(this);

    this.style.display = 'none';
    this.setAttribute('aria-hidden', 'true');
    this.setAttribute('inert', '');
    this.dispatchEvent(new CustomEvent('drawer:closed', { bubbles: true }));
    this.onClosed?.();
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  refreshScrollableElement() {
    const scrollableEl = this.querySelector('[data-scrollable]') || this;
    if (this.#scrollableEl === scrollableEl) return;

    if (this.isOpen && this.#scrollableEl) {
      enableBodyScroll(this.#scrollableEl);
    }

    this.#scrollableEl = scrollableEl;

    if (this.isOpen && this.#scrollableEl) {
      disableBodyScroll(this.#scrollableEl, bodyScrollLockOptions);
    }
  }

  // ---------- Internals ----------

  #bindTriggers() {
    if (this.#onTriggerClick) return;

    const id = this.id;
    if (!id) {
      console.warn('<drawer-component>: missing id', this);
      return;
    }

    this.#onTriggerClick = (event) => {
      const trigger = event.target.closest('[data-toggle-id="' + id + '"]');
      if (!trigger) return;
      const action = trigger.dataset.action;
      if (!['open-drawer', 'close-drawer', 'toggle-drawer'].includes(action)) return;

      event.preventDefault();
      if (action === 'open-drawer') this.open();
      else if (action === 'close-drawer') this.close();
      else this.toggle();
    };

    document.body.addEventListener('click', this.#onTriggerClick);
  }

  #unbindTriggers() {
    if (!this.#onTriggerClick) return;

    document.body.removeEventListener('click', this.#onTriggerClick);
    this.#onTriggerClick = null;
  }

  #attachGlobalListeners() {
    this.#onOverlayClick = () => this.close();
    this.#overlayEl?.addEventListener('click', this.#onOverlayClick);

    this.#onEscape = (e) => {
      if (e.key === 'Escape') this.close();
    };
    document.addEventListener('keydown', this.#onEscape);

    this.#onOtherDrawerOpening = (e) => {
      const { drawerId, isChild } = e.detail || {};
      if (drawerId === this.id) return;
      if (isChild) return;
      this.close();
    };
    document.addEventListener('drawer:opening', this.#onOtherDrawerOpening);
  }

  #detachGlobalListeners() {
    if (this.#onOverlayClick) {
      this.#overlayEl?.removeEventListener('click', this.#onOverlayClick);
      this.#onOverlayClick = null;
    }
    if (this.#onEscape) {
      document.removeEventListener('keydown', this.#onEscape);
      this.#onEscape = null;
    }
    if (this.#onOtherDrawerOpening) {
      document.removeEventListener('drawer:opening', this.#onOtherDrawerOpening);
      this.#onOtherDrawerOpening = null;
    }
  }

  /**
   * Sync ARIA state on every `data-action="toggle-drawer"` trigger that
   * controls this drawer. Keeps the disclosure contract honest so CSS
   * (e.g. icon swap via `[aria-expanded="true"]`) and screen readers stay
   * in sync with the drawer's open/closed state.
   *
   * Triggers with `data-action="open-drawer"` / `"close-drawer"` are
   * one-directional and intentionally left untouched.
   */
  #syncTriggerState(expanded) {
    if (!this.id) return;
    const triggers = document.querySelectorAll(
      `[data-toggle-id="${this.id}"][data-action="toggle-drawer"]`
    );
    triggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      // Prefer localized labels passed from Liquid via data-* (already run
      // through `| t`). Fall back to the legacy literal 'Open'/'Close' flip.
      const { labelOpen, labelClose } = trigger.dataset;
      if (labelOpen && labelClose) {
        trigger.setAttribute('aria-label', expanded ? labelClose : labelOpen);
        return;
      }
      const label = trigger.getAttribute('aria-label');
      if (label === 'Open' || label === 'Close') {
        trigger.setAttribute('aria-label', expanded ? 'Close' : 'Open');
      }
    });
  }

  #lockScroll() {
    if (this.#scrollableEl) {
      disableBodyScroll(this.#scrollableEl, bodyScrollLockOptions);
    }
    document.body.classList.add('scrollLock');
    IOSScrollLock.lock();
  }

  #unlockScroll() {
    if (this.#scrollableEl) {
      enableBodyScroll(this.#scrollableEl);
    }
    document.body.classList.remove('scrollLock');
    IOSScrollLock.unlock();
  }
}

if (!customElements.get('drawer-component')) {
  customElements.define('drawer-component', DrawerComponent);
}
